/**
 * Générateur — Automatisme : reconnaître la base d'un prisme donné en
 * perspective cavalière.
 * Attendu ID : 1116 | Niveau : 4ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2026, Représentation de l'espace 4e).
 * Labels (cartes exerciseur) :
 *   - La forme de la base            (QCM : un prisme, quelle est la forme de sa base ?)
 *   - Retrouver le prisme            (QCM : quatre prismes A à D, lequel a pour base un … ?)
 *   - Vrai ou faux ?                 (V/F sur un prisme : forme et côtés de la base, position des bases, face du dessous)
 *   (Compter et Les prismes : vrai ou faux ? RETIRÉS, décision prof 2026-09-11)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Représentation de l'espace 4e + grille P1-P16.
 * A. P1 : CM2 introduit (prisme parmi les solides usuels), 6e consolidé
 * (pavé : faces, arêtes, sommets), 5e étendu (prisme droit : bases, faces
 * latérales, patron, perspective cavalière), 4e automatisme. P2 : sens
 * installés — deux bases superposables, faces latérales rectangulaires ; sens
 * à installer — la base n'est pas forcément « en bas » : un prisme couché a
 * ses bases devant et derrière ; le pavé est un prisme dont la base est un
 * rectangle. Distracteurs : prendre une face latérale (rectangle) pour la
 * base, prendre la face d'en bas d'un prisme couché, disque (cylindre). P3 :
 * nombres de côtés 3 à 6. P4 : sans objet.
 * B. P5 : perspective cavalière (prismes debout ou couchés, arêtes cachées
 * en pointillés), moteurs svgSolides 5e et svgSolides4e. P6 : aucun
 * instrument. P7 : pas de volume ici (1117). P8 : base, face latérale,
 * prisme droit, polygone, perspective cavalière.
 * C. P9 : reconnaître les solides (1115), nommer les polygones (6e). P10 :
 * constat. P11 : un prisme couché (bases devant et derrière) ; le pavé a
 * trois paires de faces qui peuvent servir de bases. P12 : rémanence forte.
 * D. P13 : erreurs → leurres : « un rectangle » pour un prisme à base
 * triangulaire (face latérale prise pour base), polygone à un côté de plus ou
 * de moins, « un disque » (cylindre), nombre de faces latérales confondu
 * avec le nombre de faces. P14 : aucun calcul. P15 : pour le
 * pavé, la réponse « un rectangle » vaut pour toute base possible (toutes les
 * faces sont des rectangles) ; pour le cube, « un carré » ; jamais de
 * planche avec deux prismes de même base. P16 : QCM à formes distinctes ;
 * QCM sur planche à options A-D dans l'ordre.
 * INVARIANTS : toute figure porte data-solides / data-base pour les solveurs ;
 * V/F sans crochets, mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { paveNomme, prismeTri } from '../../5eme/espace/svgSolides';
import { prismePoly, planche } from './svgSolides4e';

const ATTENDU_ID = 1116;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FORME = 'La forme de la base';
const L_RETROUVER = 'Retrouver le prisme';
const L_VF = 'Vrai ou faux ?';

// ─── Prismes disponibles ───────────────────────────────────────────────────
// base : forme de la base ; n : nombre de côtés ; position : debout (bases en haut et en bas) ou couché (devant et derrière)
const PRISMES = {
    tri_couche: { base: 'un triangle', n: 3, position: 'couché' },
    tri_debout: { base: 'un triangle', n: 3, position: 'debout' },
    pave: { base: 'un rectangle', n: 4, position: 'debout' },
    cube: { base: 'un carré', n: 4, position: 'debout' },
    penta: { base: 'un pentagone', n: 5, position: 'debout' },
    hexa: { base: 'un hexagone', n: 6, position: 'debout' },
};
const FORMES = ['un triangle', 'un rectangle', 'un carré', 'un pentagone', 'un hexagone', 'un disque'];
const dessiner = (type, salt = randInt(1, 999)) => {
    const P = PRISMES[type]; let svg;
    if (type === 'tri_couche') svg = prismeTri({ salt }).svg;
    else if (type === 'tri_debout') svg = prismePoly(3, salt);
    else if (type === 'pave') svg = paveNomme({ L: randomChoice([4, 5]), H: randomChoice([2, 2.5]), P: randomChoice([2.5, 3]) }, { labels: false, salt }).svg;
    else if (type === 'cube') svg = paveNomme({ L: 3, H: 3, P: 3 }, { labels: false, salt }).svg;
    else svg = prismePoly(P.n, salt);
    return { type, ...P, svg };
};
const tirerType = () => randomChoice(Object.keys(PRISMES));
const figureSeule = (sol) => sol.svg.replace('<svg ', `<svg data-solides="${sol.type}" data-base="${sol.n}" `);

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Une forme fausse n'a de code que si c'est une vraie erreur : une face latérale (un rectangle) ou le contour du dessin (deux côtés de plus).
const COTES = { 'un triangle': 3, 'un rectangle': 4, 'un carré': 4, 'un pentagone': 5, 'un hexagone': 6 };
const codeForme = (prisme, forme) => {
    if (forme === 'un rectangle' && prisme.base !== 'un rectangle' && prisme.base !== 'un carré') return 'bases_par_position';
    if (COTES[forme] === prisme.n + 2) return 'contour_pris_pour_base';
    return null;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refF = { v: null }; const refR = { v: null };

// ─── Carte 1 : la forme de la base (QCM) ───────────────────────────────────

const construireForme = () => {
    const sol = dessiner(tirerType());
    const bonne = sol.base;
    // leurres : un rectangle (face latérale prise pour base) en priorité quand la base n'est pas un rectangle, puis les autres formes
    const prioritaires = sol.base === 'un rectangle' || sol.base === 'un carré' ? [] : ['un rectangle'];
    const autres = shuffle(FORMES.filter((f) => f !== bonne && !prioritaires.includes(f) && !(sol.type === 'cube' && f === 'un rectangle') && !(sol.type === 'pave' && f === 'un carré')));
    const options = shuffle([bonne, ...prioritaires, ...autres].slice(0, 4));
    const payload = makeQcm(options, bonne, `<p>Ce solide est un prisme droit${sol.position === 'couché' ? ', posé sur une face latérale' : ''}. Quelle est la forme de sa base ?</p>`, { ...opts(L_FORME), elements_additionnels: imgElement(figureSeule(sol), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: sol.type };
    payload._meta.params = { type: sol.type, signature: `${sol.type}-${[...options].sort().join('|')}` };
    const erreurs = options.filter((o) => o !== bonne && codeForme(sol, o)).map((o) => ({ code: codeForme(sol, o), valeur: o }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genForme = () => antiRepeat(construireForme, refF);

// ─── Carte 3 : retrouver le prisme (QCM sur planche) ───────────────────────

const construireRetrouver = () => {
    // quatre prismes de bases DISTINCTES (jamais deux triangles, ni pavé et cube ensemble : le carré est un rectangle)
    const groupes = [['tri_couche', 'tri_debout'], ['pave', 'cube'], ['penta'], ['hexa']];
    const types = shuffle(groupes.map((g) => randomChoice(g)));
    const cible = randomChoice(types);
    const lettres = ['A', 'B', 'C', 'D'];
    const solides = types.map((t, i) => ({ ...dessiner(t), label: lettres[i] }));
    const bonne = solides.find((s) => s.type === cible).label;
    const payload = makeQcm(lettres, bonne, `<p>Ces quatre solides sont des prismes droits. Lequel a pour base ${PRISMES[cible].base} ?</p>`, { ...opts(L_RETROUVER), elements_additionnels: imgElement(planche(solides), 'large') });
    payload.contenu.choix = lettres; payload.contenu.bonne_reponse = bonne;
    payload.contenu = { ...payload.contenu, sousType: cible };
    payload._meta.params = { cible, types, signature: `${cible}-${types.join(',')}` };
    // La lettre choisie : la base demandée a été prêtée au prisme de cette lettre.
    const erreurs = solides.filter((s) => s.label !== bonne && codeForme(s, PRISMES[cible].base)).map((s) => ({ code: codeForme(s, PRISMES[cible].base), valeur: s.label }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genRetrouver = () => antiRepeat(construireRetrouver, refR);

// ─── Carte 4 : vrai ou faux ? (sur un prisme) ──────────────────────────────

let derniereEmpreinteVf1116 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const sol = dessiner(tirerType());
        const fausseForme = randomChoice(FORMES.filter((f) => f !== sol.base && !(sol.type === 'cube' && f === 'un rectangle')));
        const autreNombre = (v) => randomChoice([v + 1, v - 1, v + 2].filter((x) => x >= 3 && x !== v));
        // Recentré sur la reconnaissance de la base (prof, 2026-09-10) : forme, nombre de côtés, position des bases,
        // « la face du dessous est une base » (le piège du prisme couché), « toutes les faces peuvent servir de base » (pavé, cube).
        const rect = sol.type === 'pave' || sol.type === 'cube';
        const familles = [
            { f: 'base', vrai: `La base de ce prisme est ${sol.base}.`, faux: `La base de ce prisme est ${fausseForme}.` },
            { f: 'cotes', vrai: `La base de ce prisme a ${sol.n} côtés.`, faux: `La base de ce prisme a ${autreNombre(sol.n)} côtés.` },
            { f: 'laterales', vrai: `Ce prisme a ${sol.n} faces latérales, autant que de côtés à sa base.`, faux: `Ce prisme a ${autreNombre(sol.n)} faces latérales.` },
            { f: 'position', vrai: sol.position === 'couché' ? 'Ses bases sont devant et derrière.' : 'Ses bases sont en haut et en bas.', faux: sol.position === 'couché' ? 'Ses bases sont en haut et en bas.' : 'Ses bases sont devant et derrière.' },
            { f: 'dessous', vrai: sol.position === 'couché' ? 'La face du dessous est une face latérale, pas une base.' : 'La face du dessous est une base.', faux: sol.position === 'couché' ? 'La face du dessous est une base.' : 'La face du dessous est une face latérale, pas une base.' },
            { f: 'toutes_faces', vrai: rect ? 'Toutes les faces de ce prisme peuvent servir de base.' : 'Seules deux faces de ce prisme peuvent servir de base.', faux: rect ? 'Seules deux faces de ce prisme peuvent servir de base.' : 'Toutes les faces de ce prisme peuvent servir de base.' },
        ];
        // pour le pavé et le cube, toutes les faces sont des rectangles : la position des bases n'est pas tranchée
        const dispo = familles.filter((fa) => !((fa.f === 'position' || fa.f === 'dessous') && rect));
        const retenues = shuffle(dispo).slice(0, 4); if (retenues.length < 4) continue;
        const nbFaux = randInt(1, 3);
        const ordre = shuffle(retenues); const lignes = ordre.map((fa, i) => (i < nbFaux ? { l: fa.faux, v: false, f: fa.f } : { l: fa.vrai, v: true, f: fa.f }));
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1116) continue;
        derniereEmpreinteVf1116 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>Ce solide est un prisme droit. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(figureSeule(sol), 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { type: sol.type, nbFaux };
        // Code de l'affirmation fausse de chaque famille : forme ou nombre de côtés du contour (triangle, pavé, cube), bases repérées par leur place.
        const codeFaux = {
            base: codeForme(sol, fausseForme),
            cotes: sol.n + 2 <= 6 && familles[1].faux === `La base de ce prisme a ${sol.n + 2} côtés.` ? 'contour_pris_pour_base' : null,
            position: 'bases_par_position',
            dessous: 'bases_par_position',
            toutes_faces: rect ? null : 'bases_par_position',
        };
        const erreurs = lignes.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1116 : V/F sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    forme_qcm_unique: genForme,
    retrouver_qcm_unique: genRetrouver,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', saisie: 'Saisie', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'forme', label: L_FORME, formatsList: buildFormats('forme') },
    { id: 'retrouver', label: L_RETROUVER, formatsList: buildFormats('retrouver') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    vf_tableau_vf: ['contour_pris_pour_base', 'bases_par_position'],
    retrouver_qcm_unique: ['contour_pris_pour_base', 'bases_par_position'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { PRISMES, dessiner };
